import { Box, Button, Modal, OutlinedInput } from '@mui/material';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import styled from 'styled-components';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useDispatch } from 'react-redux';
import { fetchBatchesDropdown } from '@Features/dropdowns/dropdownsSlice';
import { useTypedSelector } from '@Features/store';
import EdNumericInput from '@Components/UI/Inputs/EdNumericInput/EdNumericInput';
import { Spacer } from '@Styled/utilities';
import { scratchCardsGenerate } from '../Slices/ScratchCards.slice';
import { useSnackbar } from '@Providers/useSnackbar';
import TargetModePicker, {
	buildTargetPayload,
	emptyToNull,
	GenerationMode,
	TARGET_SCHEMA_FIELDS,
} from './TargetModePicker';

type Props = {
	onConfirmation: any;
	handleModelClose: () => void;
	openModel: boolean;
	classRooms: any;
};

export type CourseFormData = {
	classroom_id: string;
	course_id: number | null;
	standalone_course_id: number | null;
	bundle_id: number | null;
	quantity: number;
	scheme: string;
	instructor_id: number | null;
	value: number;
};

// Mirrors the backend cap (rules.range(1, 10000)): the generator builds the
// whole batch in memory before inserting, so quantity cannot be unbounded.
const QUANTITY_MAX = 10000;

const SCHEMA = yup.object().shape({
	// The mode/target half is shared with the Edit and Bulk Edit modals.
	...TARGET_SCHEMA_FIELDS,
	scheme: yup.string().required(),
	checker: yup.boolean(),
	// nullable() must precede required(): the transform maps a cleared field to
	// null, and without nullable yup's type check rejects it first — surfacing
	// its developer-facing cast message instead of a clean "required". The
	// typeError covers what the transform doesn't: pasted non-numeric text.
	quantity: yup
		.number()
		.label('Quantity')
		.transform(emptyToNull)
		.nullable()
		.typeError('Quantity must be a number')
		.positive()
		.integer()
		.max(QUANTITY_MAX)
		.required(),
});

const GenerateCodes: React.FC<Props> = ({
	handleModelClose,
	onConfirmation,
	openModel,
	classRooms,
}) => {
	const dispatch = useDispatch();
	const {
		formState: { isValid, errors, isSubmitting },
		getValues,
		watch,
		reset,
		control,
		handleSubmit,
		setValue,
	} = useForm<any>({
		resolver: yupResolver(SCHEMA),
		mode: 'onTouched',
		defaultValues: {
			scheme: 'numeric',
			checker: false,
			classroom_id: null,
			quantity: null,
			instructor_id: null,
			value: null,
			standalone_course_id: null,
			bundle_id: null,
		},
	});

	const { loaders } = useTypedSelector((state) => state.ScratchCards);
	const { displaySnackbar } = useSnackbar();

	const mode: GenerationMode = watch('mode');

	const onSubmitForm = async (formData: CourseFormData) => {
		const payload: Record<string, any> = {
			quantity: formData.quantity,
			scheme: formData.scheme,
			...buildTargetPayload(mode, formData),
		};

		// Surface failures instead of swallowing them, and keep the modal open so
		// the input is not lost. A silent `.then` here is what hid the broken
		// standalone dropdown for so long.
		try {
			await (dispatch(scratchCardsGenerate(payload as any)) as any).unwrap();
			dispatch(fetchBatchesDropdown());
			closeAndReset();
		} catch (error: any) {
			displaySnackbar(
				'error',
				error?.response?.data?.message ||
					error?.message ||
					'Unable to generate codes'
			);
		}
	};

	// Every path out of the modal drops the draft. A backdrop/Esc close used to
	// skip reset(), so the confirmation tick survived invisibly (the checkbox
	// remounts unticked) and a reopened modal was already submittable.
	const closeAndReset = () => {
		reset();
		handleModelClose();
	};

	return (
		<>

			<Modal
				open={openModel}
				onClose={closeAndReset}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Container>
					<form onSubmit={handleSubmit(onSubmitForm, (e) => console.log(e))}>
						<Box sx={{ marginLeft: '1.5rem', marginTop: '2.25rem' }}>
							<TitleStyled id="modal-modal-title"> Generate Codes </TitleStyled>
						</Box>

						<Box
							sx={{
								marginLeft: '3.5rem',
								marginRight: '4rem',
								marginTop: '1.5rem',

								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<TargetModePicker
								control={control}
								setValue={setValue}
								watch={watch}
								classRooms={classRooms}
								active={openModel}
							/>

							<Controller
								control={control}
								name="quantity"
								render={({ field, fieldState }) => (
									<Spacer my="0.5rem">
										<EdNumericInput
											fullWidth
											size="small"
											label="Set Quantity"
											placeholder="Quantity"
											error={fieldState.invalid}
											helperText={fieldState.error?.message}
											{...field}
											variant="outlined"
										/>
									</Spacer>
								)}
							/>
							<Controller
								control={control}
								name="scheme"
								defaultValue={getValues('scheme')}
								render={({ field }) => (
									<>
										<FormControl
											size="small"
											sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
										>
											<InputLabel id="scheme-select-label">Scheme</InputLabel>
											<SelectStyled
												labelId="scheme-select-label"
												id="scheme-select"
												{...field}
												// Must match the InputLabel text — MUI sizes the outline
												// notch from this prop, not from the label element.
												label="Scheme"
											>
												<MenuItem value="alpha numeric">Alpha Numeric</MenuItem>
												<MenuItem value="numeric">Numeric</MenuItem>
											</SelectStyled>
										</FormControl>
									</>
								)}
							/>
							<Controller
								control={control}
								name="checker"
								render={({ field }) => (
									<CheckboxStyled
										control={<Checkbox />}
										{...field}
										// Controlled — otherwise reset() clears the form value but
										// leaves the box visually ticked (or vice versa on remount).
										checked={!!field.value}
										label="All selected options are correct"
									/>
								)}
							/>
						</Box>
						<ButtonsContainer>
							<Button
								variant="text"
								color="inherit"
								sx={{
									fontFamily: 'Roboto',
									fontSize: '14px',
									padding: '6px 16px',
									color: '#6c63ff',
									backgroundColor: '#fff',
									outline: 'none',
								}}
								// Codes are generated in bulk and cannot be un-generated: require
								// every field valid AND the explicit confirmation tick, and lock
								// the button while a generation is in flight — the bulk insert +
								// CSV response is slow enough to double-click, and every extra
								// click is a whole new batch.
								disabled={!isValid || !watch('checker') || isSubmitting}
								type="submit"
							>
								Submit
							</Button>
							<Button
								variant="text"
								color="warning"
								sx={{
									fontFamily: 'Roboto',
									fontSize: '14px',
									padding: '6px 16px',
									color: '#ff9800',
									outline: 'none',
									backgroundColor: '#fff',
								}}
								onClick={closeAndReset}
							>
								Cancel
							</Button>
						</ButtonsContainer>
					</form>
				</Container>
			</Modal>
		</>
	);
};

export default GenerateCodes;

export const Container = styled(Box)`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	/* Was min-width only, at 23.625rem. The instructor mode's wildcard note
	   wrapped to three lines in that width and collided with the field below it.
	   A wider box lets it breathe; max-width keeps it sane on a large screen and
	   the vh cap makes a tall mode scroll instead of running off-screen. */
	width: 34rem;
	max-width: calc(100vw - 2rem);
	min-height: 21rem;
	max-height: calc(100vh - 4rem);
	overflow-y: auto;
	border-radius: 4px;
	border: 0;
	box-shadow: 0 3px 3px 0 rgba(0, 0, 0, 0.38);
	margin: 0;
	padding: 12px 0 0;
	background-color: #ffffff;
	outline: none;
	/* The app theme absolute-positions all helper text so error messages never
	   shift page layouts — inside this modal that made every message (the
	   wildcard note, dropdown errors) paint over the field below it. In-flow is
	   right in a modal: it just grows. */
	.MuiFormHelperText-root {
		position: static;
	}
`;

export const TitleStyled = styled.span`
	font-weight: 500;
	font-family: Roboto;
	text-align: left;
	font-size: 1rem;
	color: rgba(0, 0, 0, 0.6);
`;

export const ButtonsContainer = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	margin-bottom: 0.5rem;
	outline: none;
	margin-top: 1.625rem;
`;

export const RadioGroupStyled = styled(RadioGroup)`
	display: flex;
	flex-direction: row;
	.MuiTypography-root {
		font-family: Roboto;
		font-size: 0.875rem;
		color: rgba(0, 0, 0, 0.87);
	}
	.MuiRadio-root.Mui-checked {
		color: #6c63ff;
	}
`;

export const RadioStyled = styled(Radio)``;

/* Were pinned at 16.2rem, which left them stranded at half width once the
   modal grew. Fluid so every control lines up on the same edge. */
export const SelectStyled = styled(Select)`
	width: 100%;
`;

export const InputStyled = styled(OutlinedInput)`
	width: 100%;
`;

export const CheckboxStyled = styled(FormControlLabel)`
	padding-left: 1.5rem;
	.MuiTypography-root {
		font-size: 0.75rem;
		font-family: Roboto;
		font-weight: normal;
	}
`;
